import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { FileText, ExternalLink, RotateCw, ArrowRight, Eye } from "lucide-react";
import { api } from "@/lib/api";
import { pricingApi, quoteApi, absoluteFileUrl } from "@/lib/phase3Entities";
import { apiError } from "@/lib/errors";
import { assetDisplay } from "@/lib/assetDisplay";
import PricingTab from "./PricingTab";
import QuotesTab from "./QuotesTab";
import BusGroupedView from "./BusGroupedView";

const fmtMoney = (v) => {
  if (v === null || v === undefined || v === "") return "—";
  const n = Number(v);
  if (Number.isNaN(n)) return String(v);
  return n.toLocaleString("en-US", { style: "currency", currency: "USD" });
};

/**
 * Consolidated Pricing & Quote workspace (single Tour Detail tab).
 *
 * Layout (top → bottom):
 *   1. Tour Basis strip (read-only anchors)
 *   2. Pricing worksheet (existing PricingTab, unchanged)
 *   3. Quote Summary card (customer-visible lines only, live preview)
 *   4. Quote Actions (Generate v1 / Create New Version / View Latest)
 *   5. Quote Versions list (existing QuotesTab, unchanged UI)
 */
export default function PricingAndQuoteWorkspace({ tour, assets, drivers, allAssets, canWritePricing, canWriteQuotes }) {
  const tourId = tour.id;
  const [defaults, setDefaults] = useState(null);
  const [summary, setSummary] = useState(null);
  const [quotes, setQuotes] = useState([]);
  const [allDrivers, setAllDrivers] = useState([]);
  // Bumped whenever any Quote-related mutation happens (Create Quote /
  // Create New Version / Send / Accept / Decline / Discard / Supersede).
  // Passed into <QuotesTab /> so the nested tab reloads its internal
  // quotes list without a manual page refresh.
  const [quoteRefreshSignal, setQuoteRefreshSignal] = useState(0);

  const loadAll = useCallback(async () => {
    try {
      // RC10s Pass 3 — no longer eagerly load drivers. The per-bus
      // AddDriverModal uses `DriverTypeahead` (server-backed
      // `GET /api/drivers?q=&page=&page_size=25`) which reaches
      // every driver in the roster via "Load More" paging. Pre-Pass-3
      // this fetched `/drivers?page_size=100` which capped the
      // dropdown at the first 100 alphabetical drivers in a 708-row
      // Preview roster, making ~85% of drivers unreachable here.
      const [d, s, q] = await Promise.all([
        api.get(`/tours/${tourId}/pricing/derived-defaults`).then((r) => r.data).catch(() => null),
        pricingApi.summary(tourId),
        quoteApi.list(tourId),
      ]);
      setDefaults(d);
      setSummary(s);
      setQuotes(q);
      setAllDrivers([]);
    } catch (e) {
      toast.error(apiError(e));
    }
  }, [tourId]);
  useEffect(() => { loadAll(); }, [loadAll]);

  // F4 · Detect Routing → Draft-Quote mileage drift. If any per-mile
  // pricing line has a quantity that differs from the current
  // tour.estimated_mileage, surface a non-silent "Update Draft Quote"
  // banner. Sent Quote versions are immutable snapshots — the button
  // only mutates the tour's current pricing lines (Draft Quote).
  const [syncing, setSyncing] = useState(false);
  const mileageDrift = useMemo(() => {
    const items = summary?.line_items || [];
    const tourMi = Number(tour?.estimated_mileage ?? 0);
    if (!tourMi) return null;
    const drift = items.filter(
      (li) =>
        (li.category_key === "fuel_estimate" || li.category_key === "engine_service") &&
        Number(li.quantity ?? 0) !== tourMi,
    );
    if (drift.length === 0) return null;
    const oldSamples = Array.from(new Set(drift.map((d) => Number(d.quantity ?? 0)))).sort((a, b) => b - a);
    return { count: drift.length, tourMi, oldSamples };
  }, [summary, tour]);
  const applyRoutingMileage = async () => {
    setSyncing(true);
    try {
      const r = await api.post(`/tours/${tourId}/pricing/sync-routing-mileage`);
      toast.success(`Updated ${r.data.updated_line_count} line${r.data.updated_line_count === 1 ? "" : "s"} to ${Number(r.data.estimated_mileage).toLocaleString()} mi.`);
      await refreshAfterQuoteMutation();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setSyncing(false);
    }
  };

  // Fan-out refresh: reload workspace-owned state AND signal <QuotesTab />
  // (which owns its own quotes state) to reload too.
  const refreshAfterQuoteMutation = useCallback(async () => {
    await loadAll();
    setQuoteRefreshSignal((n) => n + 1);
  }, [loadAll]);

  // Assigned coach / trailer / primary-driver context for the Tour Basis strip
  const assignedCoaches = assets
    .map((a) => allAssets.find((x) => x.id === a.asset_id))
    .filter((a) => a && a.asset_type !== "trailer");
  const assignedTrailers = assets
    .map((a) => allAssets.find((x) => x.id === a.asset_id))
    .filter((a) => a && a.asset_type === "trailer");
  const primaryDriver = drivers.find((d) => (d.role || "").toLowerCase().includes("primary")) || drivers[0];

  // Owner-reported mobile defect (2026-09-10 re-fix): the fixed running-
  // tally footer (rendered inside <PricingTab> as
  // `[data-testid=pricing-totals-card]`) overlaps EVERY sibling that
  // renders after PricingTab in this workspace — Quote Summary Card,
  // Quote Footer Preview, Quote Actions, and Quote Versions. The
  // previous fix only padded PricingTab's own content column, which is
  // why the owner reported "can't see the versions" — QuotesTab lives
  // OUTSIDE PricingTab.
  //
  // Fix: measure the pinned totals card via ResizeObserver on
  // `[data-testid=pricing-totals-card]` and apply padding-bottom to the
  // workspace ROOT — that root wraps ALL siblings including the
  // versions block. Gated to a `(max-width: 1023.98px)` media query so
  // the aside becomes `lg:sticky` on desktop and the padding stays 0px.
  const workspaceRef = useRef(null);
  const [totalsHeight, setTotalsHeight] = useState(0);
  const [isBelowLg, setIsBelowLg] = useState(
    () => typeof window !== "undefined"
      && window.matchMedia("(max-width: 1023.98px)").matches,
  );
  useEffect(() => {
    if (typeof window === "undefined") return;
    const mq = window.matchMedia("(max-width: 1023.98px)");
    const onChange = (e) => setIsBelowLg(e.matches);
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, []);
  useEffect(() => {
    if (typeof ResizeObserver === "undefined") return;
    // The totals card may mount slightly after us (PricingTab is a child
    // and depends on the pricing summary loading). Poll briefly until it
    // exists in the DOM, then observe. `MutationObserver` would also
    // work but polling is simpler and terminates cleanly.
    let ro = null;
    let mo = null;
    const attach = (el) => {
      ro = new ResizeObserver((entries) => {
        for (const e of entries) setTotalsHeight(e.target.offsetHeight);
      });
      ro.observe(el);
      // Seed synchronously so the first paint after the card mounts is
      // already padded.
      setTotalsHeight(el.offsetHeight);
    };
    const root = workspaceRef.current || document;
    const found = root.querySelector("[data-testid=pricing-totals-card]");
    if (found) {
      attach(found);
    } else {
      mo = new MutationObserver(() => {
        const el = (workspaceRef.current || document).querySelector(
          "[data-testid=pricing-totals-card]",
        );
        if (el) {
          attach(el);
          mo.disconnect();
          mo = null;
        }
      });
      mo.observe(workspaceRef.current || document.body, {
        childList: true, subtree: true,
      });
    }
    return () => { ro?.disconnect(); mo?.disconnect(); };
  }, [summary]);

  const rootPaddingBottom = (isBelowLg && totalsHeight)
    ? `${totalsHeight + 16}px`
    : undefined;

  return (
    <div ref={workspaceRef}
         className="space-y-6"
         style={{ paddingBottom: rootPaddingBottom }}
         data-testid="pricing-and-quote-workspace">
      <TourBasisStrip
        tour={tour}
        defaults={defaults}
        assignedCoaches={assignedCoaches}
        assignedTrailers={assignedTrailers}
        primaryDriver={primaryDriver}
      />

      {mileageDrift && (
        <div className="bg-amber-hold/10 border border-amber-hold rounded-md p-3 flex items-center gap-3"
             data-testid="routing-mileage-drift-banner">
          <RotateCw size={16} className="text-amber-hold shrink-0" />
          <div className="flex-1 text-[13px] text-carbon">
            <div className="font-medium">Routing updated — Quote mileage differs.</div>
            <div className="text-[11.5px] text-steel">
              {mileageDrift.count} pricing line{mileageDrift.count === 1 ? "" : "s"} still use{mileageDrift.count === 1 ? "s" : ""} the previous value
              {mileageDrift.oldSamples.length > 0 && (
                <> ({mileageDrift.oldSamples.map((n) => n.toLocaleString()).join(", ")} mi)</>
              )}.
              Current tour mileage: <strong className="text-carbon">{mileageDrift.tourMi.toLocaleString()} mi</strong>.
              Sent Quote versions are immutable and will not change.
            </div>
          </div>
          {canWriteQuotes && (
            <button onClick={applyRoutingMileage} disabled={syncing}
                    className="bg-coach-blue text-white text-[12px] font-medium px-3 py-1.5 rounded-sm disabled:opacity-40"
                    data-testid="routing-mileage-drift-apply">
              {syncing ? "Updating…" : "Update Draft Quote"}
            </button>
          )}
        </div>
      )}

      <BusGroupedView
        tour={tour}
        summary={summary}
        assets={assets}
        drivers={drivers}
        allAssets={allAssets}
        allDrivers={allDrivers}
        canWrite={canWritePricing}
        canAssign={canWritePricing}
        refresh={loadAll}
      />

      <PricingTab tourId={tourId} canWrite={canWritePricing} />

      <QuoteSummaryCard tour={tour} summary={summary} assignedCoaches={assignedCoaches}
                        assignedTrailers={assignedTrailers} defaults={defaults} />

      <QuoteFooterPreviewPanel tourId={tourId} />

      <QuoteActions tour={tour} quotes={quotes} summary={summary}
                    canWrite={canWriteQuotes} refresh={refreshAfterQuoteMutation} />

      <div>
        <div className="text-[10.5px] uppercase tracking-widest text-steel mb-2">Quote Versions</div>
        <QuotesTab tourId={tourId} canWrite={canWriteQuotes}
                   customerId={tour?.customer_id}
                   refreshSignal={quoteRefreshSignal}
                   onChanged={refreshAfterQuoteMutation} />
      </div>
    </div>
  );
}

// ---------------- Tour Basis strip ----------------
const TourBasisStrip = ({ tour, defaults, assignedCoaches, assignedTrailers, primaryDriver }) => {
  const cells = [
    { k: "Start Date",   v: tour.start_date || "—" },
    { k: "End Date",     v: tour.end_date   || "—" },
    { k: "Billed Days",  v: defaults?.billed_days ?? tour.billed_days ?? tour.tour_days ?? "—" },
    { k: "Estimated Miles", v: defaults?.estimated_mileage ?? tour.estimated_mileage ?? "—" },
    { k: "Coach(es)",    v: assignedCoaches.length ? assignedCoaches.map(assetDisplay).join(" · ") : "—" },
    { k: "Trailer(s)",   v: assignedTrailers.length ? assignedTrailers.map(assetDisplay).join(" · ") : "—" },
    { k: "Primary Driver", v: primaryDriver ? (primaryDriver.driver_name || primaryDriver.driver_id) : "—" },
  ];
  const anyMissing = cells.some((c) => c.v === "—");
  return (
    <div className="bg-white border border-coach-silver rounded-md p-4" data-testid="tour-basis-strip">
      <div className="flex items-baseline justify-between mb-3">
        <div className="text-[10.5px] uppercase tracking-widest text-steel font-medium">Tour Basis</div>
        {anyMissing && (
          <a href="#overview" onClick={(e) => { e.preventDefault(); document.querySelector('[data-testid="tour-tab-overview"]')?.click(); }}
             className="text-[11px] text-coach-blue hover:underline" data-testid="tour-basis-add-hint">
            Missing values — add on Overview →
          </a>
        )}
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3">
        {cells.map((c, i) => (
          <div key={i} className="min-w-0" data-testid={`basis-${c.k.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
            <div className="text-[10px] uppercase tracking-widest text-steel">{c.k}</div>
            <div className={`text-[13px] truncate ${c.v === "—" ? "text-steel italic" : "text-carbon"}`}>{c.v}</div>
          </div>
        ))}
      </div>
    </div>
  );
};

// ---------------- Quote Summary (live preview, customer-visible lines only) ----------------
const QuoteSummaryCard = ({ tour, summary, assignedCoaches, assignedTrailers }) => {
  // Filter: only customer-facing lines. Exclude internal_cost + all_access billing.
  const visibleLines = (summary?.line_items || []).filter((li) => {
    if (li.category === "internal_cost") return false;
    if (li.billing_responsibility === "all_access") return false;
    return li.customer_visible_effective !== false;
  });
  return (
    <div className="bg-white border border-coach-silver rounded-md p-4" data-testid="quote-summary-card">
      <div className="flex items-baseline justify-between mb-3">
        <div className="text-[10.5px] uppercase tracking-widest text-steel font-medium">Quote Summary (customer-facing preview)</div>
        <div className="text-[10.5px] text-steel italic">Internal-cost and All-Access-billed rows are excluded from this preview.</div>
      </div>
      <div className="grid md:grid-cols-2 gap-4 text-[13px] mb-3">
        <div>
          <div className="text-[10px] uppercase tracking-widest text-steel">Customer / Artist</div>
          <div className="text-carbon">{tour.artist_display || tour.customer_id || "—"}</div>
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-widest text-steel">Tour</div>
          <div className="text-carbon truncate">{tour.display_name} <span className="text-steel font-mono text-[11px]">{tour.tour_number}</span></div>
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-widest text-steel">Dates</div>
          <div className="text-carbon">
            {tour.start_date && tour.end_date ? `${tour.start_date} → ${tour.end_date}` : <span className="text-steel italic">Not Added</span>}
          </div>
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-widest text-steel">Coach / Trailer</div>
          <div className="text-carbon truncate">
            {assignedCoaches.map(assetDisplay).join(" · ") || "—"}
            {assignedTrailers.length > 0 && <span className="text-steel"> · {assignedTrailers.map(assetDisplay).join(" · ")}</span>}
          </div>
        </div>
      </div>

      {visibleLines.length === 0 ? (
        <div className="text-[12.5px] text-steel italic border-t border-coach-silver/60 pt-3">
          No customer-facing pricing lines yet.
        </div>
      ) : (
        <table className="w-full text-[12.5px] border-t border-coach-silver/60" data-testid="quote-summary-lines">
          <thead className="text-[10px] uppercase tracking-widest text-steel">
            <tr>
              <th className="text-left py-1.5">Item</th>
              <th className="text-right py-1.5 w-16">Qty</th>
              <th className="text-left py-1.5 w-20">Unit</th>
              <th className="text-right py-1.5 w-24">Rate</th>
              <th className="text-right py-1.5 w-28">Amount</th>
            </tr>
          </thead>
          <tbody>
            {visibleLines.map((li) => (
              <tr key={li.id} className="border-t border-coach-silver/50" data-testid={`summary-line-${li.id}`}>
                <td className="py-1 text-carbon">{li.customer_facing_description || li.description}</td>
                <td className="py-1 text-right tabular text-carbon">{li.quantity}</td>
                <td className="py-1 text-steel">{(li.unit_of_measure || li.unit || "").replace(/_/g, " ")}</td>
                <td className="py-1 text-right tabular text-carbon">{fmtMoney(li.rate)}</td>
                <td className="py-1 text-right tabular font-medium">{fmtMoney(li.computed_amount)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      <div className="border-t border-coach-silver/60 mt-3 pt-3 grid md:grid-cols-3 gap-3">
        <Total label="Included in Contract" value={fmtMoney(summary?.contract_total)} />
        <Total label="Client Responsible"   value={fmtMoney(summary?.customer_total)} />
        <Total label="Quote Total"          value={fmtMoney(summary?.customer_total)} strong />
      </div>
      {tour.quote_footer_text && (
        <div className="text-[11.5px] text-steel italic mt-3 border-t border-coach-silver/60 pt-2 whitespace-pre-wrap">
          {tour.quote_footer_text}
        </div>
      )}
    </div>
  );
};

const Total = ({ label, value, strong }) => (
  <div>
    <div className="text-[10px] uppercase tracking-widest text-steel">{label}</div>
    <div className={`tabular ${strong ? "text-deep-road-navy font-display text-[18px]" : "text-carbon text-[14px] font-medium"}`}>{value}</div>
  </div>
);

// ---------------- Quote Actions ----------------
const QuoteActions = ({ tour, quotes, summary, canWrite, refresh }) => {
  const [busy, setBusy] = useState(false);
  const latestSent = quotes.find((q) => q.status === "sent");
  const latestAny = quotes[0];

  // Detect: are the current pricing totals identical to the latest sent quote's snapshot?
  const currentTotal = String(summary?.customer_total ?? "");
  const sentSnapTotal = String(latestSent?.snapshot_pricing?.totals?.customer_total ?? "");
  const pricingUnchangedVsLatestSent = latestSent && currentTotal === sentSnapTotal && currentTotal !== "";

  const generate = async () => {
    if (!canWrite) return;
    setBusy(true);
    try {
      await quoteApi.create(tour.id, {});
      toast.success("Quote generated");
      await refresh();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy(false);
    }
  };

  let primary = null;
  if (!latestAny) {
    primary = { label: "Create Quote", icon: <FileText size={14} />, onClick: generate, testid: "quote-create" };
  } else if (latestSent && pricingUnchangedVsLatestSent) {
    primary = {
      label: `View Latest Quote (Version ${latestSent.version})`,
      icon: <ExternalLink size={14} />,
      onClick: () => latestSent.pdf_url && window.open(absoluteFileUrl(latestSent.pdf_url), "_blank"),
      testid: "quote-view-latest",
    };
  } else {
    primary = {
      label: "Create New Version",
      icon: <RotateCw size={14} />,
      onClick: generate,
      testid: "quote-create-new-version",
    };
  }

  const nextVersion = (latestAny?.version || 0) + 1;

  return (
    <div className="bg-white border border-coach-silver rounded-md p-4 flex items-center justify-between gap-3" data-testid="quote-actions-card">
      <div className="text-[12.5px] text-steel min-w-0 flex items-center gap-2">
        {!latestAny && (
          <>
            <span className="text-[10px] uppercase tracking-widest text-carbon border border-coach-silver rounded-sm px-1.5 py-0.5" data-testid="quote-version-badge">
              Version 1
            </span>
            <span data-testid="quote-status-line">No quote has been generated yet. Ensure pricing is complete before issuing.</span>
          </>
        )}
        {latestAny && latestSent && pricingUnchangedVsLatestSent && (
          <span data-testid="quote-status-line">
            Pricing matches sent Quote Version {latestSent.version}. No new version needed.
          </span>
        )}
        {latestAny && !(latestSent && pricingUnchangedVsLatestSent) && (
          <>
            <span className="text-[10px] uppercase tracking-widest text-carbon border border-coach-silver rounded-sm px-1.5 py-0.5" data-testid="quote-version-badge">
              Version {nextVersion}
            </span>
            <span data-testid="quote-status-line">
              Live pricing has changed since the latest quote (Version {latestAny.version}, {latestAny.status}). Issue a new version to sync.
            </span>
          </>
        )}
      </div>
      {canWrite && primary && (
        <button
          onClick={primary.onClick}
          disabled={busy}
          data-testid={primary.testid}
          className="inline-flex items-center gap-1.5 h-9 px-4 rounded-sm text-[12.5px] font-semibold bg-coach-blue hover:bg-deep-road-navy text-white disabled:opacity-60"
        >
          {primary.icon}
          {busy ? "Working…" : primary.label}
          <ArrowRight size={13} />
        </button>
      )}
    </div>
  );
};


// ---------------- Quote Footer Preview (resolver preview only) ----------------

const FOOTER_SOURCE_LABEL = {
  tour_override: "Tour Override",
  standard_template: "Standard Template",
  non_all_inclusive_template: "Non-All-Inclusive Template",
  empty: "No Footer",
};

/**
 * QuoteFooterPreviewPanel
 *
 * Shows the EXACT footer text that will be captured on the next quote
 * generation for this tour. Reads through the server-side resolver at
 * `/api/reports/_footer-preview/{tour_id}` so precedence semantics
 * (tour override → contract_type-matched org template → empty) are
 * enforced in one place.
 *
 * IMPORTANT: this panel is READ-ONLY. It never mutates the tour, org
 * templates, or any existing quote snapshot. Editing the org template
 * in Administration → System Settings will update this preview live
 * (via the Refresh button) but will not touch snapshots already frozen
 * onto sent quote versions.
 */
const QuoteFooterPreviewPanel = ({ tourId }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    if (!tourId) return;
    setLoading(true);
    try {
      const r = await api.get(`/reports/_footer-preview/${tourId}`);
      setData(r.data);
    } catch (e) {
      setData({ error: true });
    } finally {
      setLoading(false);
    }
  }, [tourId]);
  useEffect(() => { load(); }, [load]);

  if (!tourId) {
    return (
      <div data-testid="quote-footer-preview" className="bg-white border border-coach-silver rounded-md p-3 text-[12.5px] text-steel">
        Quote footer preview is available after saving the tour.
      </div>
    );
  }

  const source = data?.source || "empty";
  const text = data?.resolved_footer_text || "";

  return (
    <div
      data-testid="quote-footer-preview"
      className="bg-white border border-coach-silver rounded-md p-3"
    >
      <div className="flex items-center justify-between mb-2">
        <div className="inline-flex items-center gap-1.5 text-[10.5px] uppercase tracking-widest text-steel font-medium">
          <Eye size={12} /> Quote Footer — preview at next generation
        </div>
        <div className="flex items-center gap-2">
          <span
            data-testid="quote-footer-preview-source"
            data-source={source}
            className="text-[10px] uppercase tracking-widest px-1.5 py-0.5 rounded-sm border border-coach-silver bg-road-white text-carbon"
          >
            {FOOTER_SOURCE_LABEL[source] || source}
          </span>
          <button
            data-testid="quote-footer-preview-refresh"
            type="button"
            onClick={load}
            disabled={loading}
            className="inline-flex items-center gap-1 h-7 px-2 rounded-sm text-[11px] text-coach-blue border border-coach-silver hover:bg-chrome-mist disabled:opacity-60"
          >
            <RotateCw size={11} className={loading ? "animate-spin" : ""} />
            Refresh
          </button>
        </div>
      </div>
      {loading ? (
        <div className="text-steel text-[12.5px]">Resolving…</div>
      ) : data?.error ? (
        <div className="text-critical-red text-[12.5px]" data-testid="quote-footer-preview-text">
          Could not load preview.
        </div>
      ) : text ? (
        <div
          data-testid="quote-footer-preview-text"
          className="whitespace-pre-wrap text-[12.5px] text-carbon bg-road-white border border-coach-silver rounded-sm px-2.5 py-2"
        >
          {text}
        </div>
      ) : (
        <div
          data-testid="quote-footer-preview-text"
          className="text-[12.5px] text-steel italic bg-road-white border border-coach-silver rounded-sm px-2.5 py-2"
        >
          — No footer text will be captured on the next generation —
        </div>
      )}
      <div className="mt-2 text-[10.5px] text-steel">
        Preview only. Does not mutate this tour, org templates, or any existing quote snapshot.
        Precedence: tour override → contract-type template → empty.
      </div>
    </div>
  );
};
